import { Typography } from '@mui/joy';
import { ArrowsLeftRight, FileCsv, FileXls, Ruler, Wrench } from '@phosphor-icons/react';
import { GridApi } from 'ag-grid-community';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UomConversionStatus } from 'companySettings/enums';
import { IUomConversionAuditRow } from 'companySettings/types';
import { AgGridTable, Button } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { ISelectionActionProps, ITableActionProps } from 'core/types';
import { useCanManageParts } from 'corporate/hooks';
import { PartDetails, PartDetailsTab } from 'parts/components/PartDetails';
import { IPart } from 'parts/types';
import { Color, Level, Variant } from 'theme/enums';

import { columnDef } from './columns';
import { UomConversionAuditColId } from './enums';
import { BulkSetUpParts } from '../BulkSetUpParts';
import { BulkUpdateUoms } from '../BulkUpdateUoms';
import { BulkUpsertUomConversions } from '../BulkUpsertUomConversions';
import { getSelectionUomPair } from '../BulkUpsertUomConversions/mappers';

const EXPORT_FILE_NAME = 'vendor-quote-uom-conversion-audit';
// File-system-safe (no colons); stamped at click time.
const EXPORT_TIMESTAMP_FORMAT = 'YYYY-MM-DD_HH-mm-ss';

const getExportFileName = (extension: string) =>
  `${EXPORT_FILE_NAME}_${dayjs().format(EXPORT_TIMESTAMP_FORMAT)}.${extension}`;

// Mirror the on-screen table in the file: displayed columns in their current
// visual order (saved layout / drags / hidden columns included), minus AG-Grid's
// internal columns (e.g. the selection checkbox) and action columns, which export
// as a labelled but permanently blank column. Recomputed at click time on purpose:
// the app-wide default (useAgGridExcelExport) memoises the same list on `gridApi`,
// whose identity never changes, so it goes stale as soon as a column is moved or
// hidden.
const getExportColumnKeys = (gridApi?: GridApi) =>
  gridApi
    ?.getAllDisplayedColumns()
    ?.filter((column) => !column.getColDef().context?.nonExportable)
    ?.map((column) => column.getColId())
    .filter((colId) => !colId.startsWith('ag-')) ?? [];

interface IProps {
  rows: IUomConversionAuditRow[];
  isLoading: boolean;
}

export const UomConversionAuditTable = ({ rows, isLoading }: IProps) => {
  const [selectedPart, setSelectedPart] = useState<IUomConversionAuditRow | null>(null);
  const [isPartDetailsOpen, setIsPartDetailsOpen] = useState(false);
  const [bulkUpdateRows, setBulkUpdateRows] = useState<IUomConversionAuditRow[]>([]);
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [bulkSetUpRows, setBulkSetUpRows] = useState<IUomConversionAuditRow[]>([]);
  const [isBulkSetUpOpen, setIsBulkSetUpOpen] = useState(false);
  const [bulkConversionRows, setBulkConversionRows] = useState<IUomConversionAuditRow[]>([]);
  const [isBulkConversionOpen, setIsBulkConversionOpen] = useState(false);

  const { t } = useTranslation();

  // Bulk assignment writes the catalogue, so it takes Manage Parts — a role can be
  // granted the audit (to see the gaps) without being allowed to change parts.
  const canBulkUpdate = useCanManageParts();

  const onPartClick = useCallback((row: IUomConversionAuditRow) => {
    setSelectedPart(row);
    setIsPartDetailsOpen(true);
  }, []);

  const columnDefs = useMemo(() => columnDef(onPartClick), [onPartClick]);

  // Kept while the panel close animation runs; the panel resolves the catalog
  // part itself from these identifiers (same behavior as quote/order part lists).
  const partDetailsPart = useMemo(
    () =>
      selectedPart &&
      ({
        id: selectedPart.partId,
        number: selectedPart.partNumber,
        manufacturerId: selectedPart.manufacturerId,
        manufacturerUrlName: selectedPart.manufacturerUrlName,
        verified: selectedPart.verified,
      } as Partial<IPart> as IPart),
    [selectedPart]
  );

  // Memoized: AgGridTableView keys a delayed-visibility timer on this element's
  // identity, so a fresh element every render defers the empty state.
  const emptyStateElement = useMemo(
    () => <Typography level={Level.BODY_MD}>{t('messageNoUomConversionGaps')}</Typography>,
    [t]
  );

  // The page is a triage list: land on the actionable rows. Only the Status
  // filter is seeded — clearing it reveals the full report. No sort model: the
  // BE sends rows pre-sorted (problem statuses first, then line count desc).
  const initialState = useMemo(
    () => ({
      filter: {
        filterModel: {
          [UomConversionAuditColId.STATUS]: {
            filterType: 'set',
            values: [UomConversionStatus.MISSING],
          },
        },
      },
    }),
    []
  );

  const onExportExcel = useCallback((gridApi?: GridApi, onlySelected = false) => {
    gridApi?.exportDataAsExcel({
      fileName: getExportFileName('xlsx'),
      onlySelected,
      columnKeys: getExportColumnKeys(gridApi),
    });
  }, []);

  const onExportCsv = useCallback((gridApi?: GridApi, onlySelected = false) => {
    gridApi?.exportDataAsCsv({
      fileName: getExportFileName('csv'),
      onlySelected,
      columnKeys: getExportColumnKeys(gridApi),
    });
  }, []);

  const getExportButtons = useCallback(
    (gridApi?: GridApi, onlySelected = false) => [
      <Button
        key="exportXlsButton"
        text={t('buttonLabelDownloadXls')}
        startDecorator={<FileXls />}
        variant={Variant.OUTLINED}
        color={Color.NEUTRAL}
        onClick={() => onExportExcel(gridApi, onlySelected)}
      />,
      <Button
        key="exportCsvButton"
        text={t('buttonLabelDownloadCsv')}
        startDecorator={<FileCsv />}
        variant={Variant.OUTLINED}
        color={Color.NEUTRAL}
        onClick={() => onExportCsv(gridApi, onlySelected)}
      />,
    ],
    [t, onExportCsv, onExportExcel]
  );

  // Toolbar buttons export all (filtered) rows; with a selection active the
  // toolbar switches to the selection bar, whose buttons export only the selection.
  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => getExportButtons(gridApi, false),
    [getExportButtons]
  );

  const onBulkUpdateClick = useCallback((rows: IUomConversionAuditRow[]) => {
    setBulkUpdateRows(rows);
    setIsBulkUpdateOpen(true);
  }, []);

  const onBulkSetUpClick = useCallback((rows: IUomConversionAuditRow[]) => {
    setBulkSetUpRows(rows);
    setIsBulkSetUpOpen(true);
  }, []);

  const onBulkConversionClick = useCallback((rows: IUomConversionAuditRow[]) => {
    setBulkConversionRows(rows);
    setIsBulkConversionOpen(true);
  }, []);

  // The selection bar is the only entry point: assigning a UOM only makes sense
  // once the user has filtered to the parts that should share it, which is the
  // workflow the header select-all (it takes the filtered rows) already supports.
  const selectionActionButtons = useCallback(
    ({ gridApi, selectedRows }: ISelectionActionProps) => {
      const rows = (selectedRows ?? []) as IUomConversionAuditRow[];

      // A No Corporate Part row has no catalogue entry to write to. Update UOMs
      // reports those back as skipped, and a conversion saved against one just
      // vanishes: the report keeps the row on No Corporate Part and the UOM tab
      // only lists conversions for set-up parts. Blocking both actions while any is
      // selected is clearer than a success message with nothing to show for it.
      const hasNoCorporatePartRow = rows.some(
        (row) => UomConversionStatus.NO_CORPORATE_PART === row.conversionToBase
      );

      // A conversion and a company part are both keyed by (part, manufacturer), so
      // a part with no manufacturer can have neither. Those rows are dropped from
      // both payloads server-side; blocking the actions while any is selected says
      // so before the user fills in a drawer -- a selection of only such rows would
      // otherwise open one reading "0 parts" whose submit does nothing.
      const hasNoManufacturerRow = rows.some((row) => null === row.manufacturerId);

      // One conversion is written to every selected part, so a selection that
      // spans different Base or Extracted UOMs would save pairs that never clear
      // some of its rows.
      const hasMixedUoms = getSelectionUomPair(rows).isMixed;

      return [
        ...(canBulkUpdate
          ? [
              <Button
                key="bulkUpdateUomsButton"
                text={t('buttonLabelUpdateUoms')}
                startDecorator={<Ruler />}
                disabled={hasNoCorporatePartRow}
                tooltipProps={
                  hasNoCorporatePartRow
                    ? { title: t('tooltipUpdateUomsNoCorporatePart'), children: null }
                    : undefined
                }
                onClick={() => onBulkUpdateClick(rows)}
              />,
              <Button
                key="bulkSetUpPartsButton"
                text={t('buttonLabelEditCompanyPartDetails')}
                startDecorator={<Wrench />}
                disabled={hasNoManufacturerRow}
                tooltipProps={
                  hasNoManufacturerRow
                    ? { title: t('tooltipEditCompanyPartDetailsNoManufacturer'), children: null }
                    : undefined
                }
                onClick={() => onBulkSetUpClick(rows)}
              />,
              <Button
                key="bulkUomConversionsButton"
                text={t('buttonLabelAddUomConversions')}
                startDecorator={<ArrowsLeftRight />}
                disabled={hasNoManufacturerRow || hasNoCorporatePartRow || hasMixedUoms}
                tooltipProps={
                  hasNoManufacturerRow
                    ? { title: t('tooltipAddUomConversionsNoManufacturer'), children: null }
                    : hasNoCorporatePartRow
                    ? { title: t('tooltipAddUomConversionsNoCorporatePart'), children: null }
                    : hasMixedUoms
                    ? { title: t('tooltipAddUomConversionsMixedUoms'), children: null }
                    : undefined
                }
                onClick={() => onBulkConversionClick(rows)}
              />,
            ]
          : []),
        ...getExportButtons(gridApi, true),
      ];
    },
    [t, canBulkUpdate, getExportButtons, onBulkUpdateClick, onBulkSetUpClick, onBulkConversionClick]
  );

  return (
    <SelectionCountProvider>
      <AgGridClientTableProvider
        tableId="uomConversionAuditTable"
        entityType="Row"
        layoutType={TableLayoutType.UOM_CONVERSION_AUDIT}
        columnDefs={columnDefs}
        initialState={initialState}
        data={rows}
        isDataLoading={isLoading}
        loadingPercentage={isLoading ? 0 : 100}
        dataCurrentPage={1}
        hasLayoutActions
        headerCheckbox
      >
        <AgGridTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
          emptyStateElement={emptyStateElement}
        />

        <BulkUpdateUoms
          selectedRows={bulkUpdateRows}
          isOpen={isBulkUpdateOpen}
          onClose={() => setIsBulkUpdateOpen(false)}
        />

        <BulkSetUpParts
          selectedRows={bulkSetUpRows}
          isOpen={isBulkSetUpOpen}
          onClose={() => setIsBulkSetUpOpen(false)}
        />

        <BulkUpsertUomConversions
          selectedRows={bulkConversionRows}
          isOpen={isBulkConversionOpen}
          onClose={() => setIsBulkConversionOpen(false)}
        />

        <PartDetails
          part={partDetailsPart}
          isOpen={isPartDetailsOpen}
          onClose={() => setIsPartDetailsOpen(false)}
          // This page audits UOM conversions, so the panel opens on UoM rather than
          // the app-wide default (History / Sales History). Local to this call site.
          defaultTab={PartDetailsTab.UOM}
          // Without a part number the Sales History tab never fires its query and sits
          // permanently empty. A quote line passes its vendor part number; an audit row
          // aggregates several lines, so the catalogue part number is what we can offer.
          salesHistoryConfig={{ partNumber: selectedPart?.partNumber }}
        />
      </AgGridClientTableProvider>
    </SelectionCountProvider>
  );
};
